import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useDataModal } from '../../../../hooks/useDataModal';
import { feedsState } from '../../../../recoil/feeds/feeds.state';
import { FeedItem } from '../../../../types/feed.types';
import Button from '../../../button/v2/button';
import ReactionsContainer from '../../../containers/reactions.container';
import { HStack } from '../../../layouts/stack.layout';
import { bookmarkToast } from '../../../toast/bookmark.toast';
import Text from '../../../typography/v2/text';
import ToolTipWrapper from '../../../utility/tooltip-wrapper';

type MediaCardFooterProps = {
  articleLink: string;
  articleId: string;
  isBookmarked: boolean;
};

const actionBookmarkFeed = (prev: FeedItem[], id: string) => {
  // find old the feed to be updated
  const oldFeed = prev.find((feed) => feed.id === id);
  if (!oldFeed) {
    return prev;
  }
  // mk cpy of the feed to be updated
  let newFeed = { ...oldFeed };
  // update the cpy of the feed to be updated
  newFeed.bookmarked = !newFeed.bookmarked;
  // remove the old feed from the list on feeds
  const filteredPrev = prev.filter((feed) => feed.id !== newFeed.id);
  // return new feeds
  return [...filteredPrev, newFeed];
};

export function MediaCardFooter(props: MediaCardFooterProps) {
  const { articleLink, isBookmarked, articleId } = props;

  const setFeeds = useSetRecoilState(feedsState);

  return (
    <HStack justify="between" extensions="items-center w-full">
      <HStack extensions="w-2/5" gap="lg">
        <ToolTipWrapper text="Bookmark">
          <Button
            onClick={() => {
              setFeeds((prev) => actionBookmarkFeed(prev, articleId));
              if (!isBookmarked) bookmarkToast('Article', 'ADD');
              if (isBookmarked) bookmarkToast('Article', 'REMOVE');
            }}
            asIcon={isBookmarked ? 'alt-saved' : 'saved'}
            color="glass"
            size="md"
          />
        </ToolTipWrapper>
      </HStack>
      <HStack extensions="w-3/5" justify="end">
        <a href={articleLink} target="_blank" rel="noreferrer">
          <Button width="fit" text="Read" size="sm" />
        </a>
      </HStack>
    </HStack>
  );
}

type SocialCardFooterProps = {
  externalLink?: string;
  isBookmarked: boolean;
  type?: 'internal' | 'external';
  id: string;
  reaction?: 'sad' | 'excited' | 'like' | 'indifferent' | null;
};

export function SocialCardFooter(props: SocialCardFooterProps) {
  const [showReactions, setShowReactions] = useState(false);
  const { externalLink, id, type, isBookmarked, reaction } = props;

  const [openShareModal] = useDataModal(id, 'SHARE');

  const setFeeds = useSetRecoilState(feedsState);

  return (
    <div className="relative rounded-b-xl">
      <HStack
        extensions={showReactions ? 'hidden' : 'flex'}
        justify={'between'}
      >
        <ToolTipWrapper text="Bookmark">
          <Button
            color="glass"
            size="base"
            withButtonStyle
            asIcon={isBookmarked ? 'alt-saved' : 'saved'}
            onClick={() => {
              setFeeds((prev) => actionBookmarkFeed(prev, id));
              if (!isBookmarked) bookmarkToast('Post', 'ADD');
              if (isBookmarked) bookmarkToast('Post', 'REMOVE');
            }}
          />
        </ToolTipWrapper>
        {externalLink && type === 'external' && (
          <ToolTipWrapper text="React">
            <a href={externalLink} target="_blank" rel="noreferrer">
              <Button
                color="glass"
                withButtonStyle
                asIcon="react"
                size="base"
              />
            </a>
          </ToolTipWrapper>
        )}
        {type !== 'external' && (
          <ToolTipWrapper text="React">
            <Button
              onClick={() => setShowReactions(true)}
              color="glass"
              withButtonStyle
              asIcon="react"
              size="base"
            />
          </ToolTipWrapper>
        )}
        <ToolTipWrapper text="Channels">
          <Button color="glass" withButtonStyle asIcon="spaces" size="base" />
        </ToolTipWrapper>
        <ToolTipWrapper text="Share">
          <Button
            color="glass"
            withButtonStyle
            asIcon="share"
            size="base"
            onClick={openShareModal}
          />
        </ToolTipWrapper>
      </HStack>
      <ReactionsContainer
        reaction={reaction}
        setShowReactions={setShowReactions}
        showReactions={showReactions}
      />
    </div>
  );
}

export function Citation({ text }: { text: string }) {
  return (
    <div className="w-full">
      <HStack gap="xs" justify="end">
        <Text text="from" />
        <Text color="primary" variant="special" text={text} />
      </HStack>
    </div>
  );
}
